import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { getAllCategories, getSubcategories } from '../data/categories';
import { X, Plus, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Composant pour sélectionner plusieurs catégories et sous-catégories
 * @param {Array} selectedCompetences - Tableau des compétences déjà sélectionnées
 * @param {Function} onChange - Fonction appelée quand les sélections changent
 */
const MultiCategorySelector = ({ selectedCompetences = [], onChange }) => {
  const [competences, setCompetences] = useState(selectedCompetences);
  const [expandedCategories, setExpandedCategories] = useState({});
  const allCategories = getAllCategories();

  useEffect(() => {
    setCompetences(selectedCompetences);
  }, [selectedCompetences]);

  const toggleCategory = (categoryId) => {
    setExpandedCategories(prev => ({
      ...prev,
      [categoryId]: !prev[categoryId]
    }));
  };

  const handleCategoryToggle = (category) => {
    const existingIndex = competences.findIndex(c => c.categoryId === category.id);
    
    if (existingIndex >= 0) {
      // Retirer la catégorie et toutes ses sous-catégories
      const newCompetences = competences.filter(c => c.categoryId !== category.id);
      setCompetences(newCompetences);
      onChange(newCompetences);
    } else {
      // Ajouter la catégorie sans sous-catégorie
      const newCompetences = [
        ...competences,
        {
          categoryId: category.id,
          categoryName: category.name,
          subcategories: []
        }
      ];
      setCompetences(newCompetences);
      onChange(newCompetences);
      // Auto-expand pour montrer les sous-catégories
      setExpandedCategories(prev => ({ ...prev, [category.id]: true }));
    }
  };

  const handleSubcategoryToggle = (categoryId, categoryName, subcategory) => {
    const existingCatIndex = competences.findIndex(c => c.categoryId === categoryId);
    
    if (existingCatIndex >= 0) {
      // La catégorie existe déjà
      const existingCat = competences[existingCatIndex];
      const subcatIndex = existingCat.subcategories.indexOf(subcategory);
      
      if (subcatIndex >= 0) {
        // Retirer la sous-catégorie
        const newSubcategories = existingCat.subcategories.filter(s => s !== subcategory);
        const newCompetences = [...competences];
        newCompetences[existingCatIndex] = {
          ...existingCat,
          subcategories: newSubcategories
        };
        setCompetences(newCompetences);
        onChange(newCompetences);
      } else {
        // Ajouter la sous-catégorie
        const newCompetences = [...competences];
        newCompetences[existingCatIndex] = {
          ...existingCat,
          subcategories: [...existingCat.subcategories, subcategory]
        };
        setCompetences(newCompetences);
        onChange(newCompetences);
      }
    } else {
      // Créer la catégorie avec cette sous-catégorie
      const newCompetences = [
        ...competences,
        {
          categoryId,
          categoryName,
          subcategories: [subcategory]
        }
      ];
      setCompetences(newCompetences);
      onChange(newCompetences);
    }
  };

  const removeCompetence = (categoryId) => {
    const newCompetences = competences.filter(c => c.categoryId !== categoryId);
    setCompetences(newCompetences);
    onChange(newCompetences);
  };

  const isCategorySelected = (categoryId) => {
    return competences.some(c => c.categoryId === categoryId);
  };

  const isSubcategorySelected = (categoryId, subcategory) => {
    const cat = competences.find(c => c.categoryId === categoryId);
    return cat?.subcategories.includes(subcategory) || false;
  };

  return (
    <div className="space-y-4">
      {/* Sélections actuelles */}
      {competences.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Mes Compétences Sélectionnées</CardTitle>
            <CardDescription>
              {competences.length} catégorie(s) sélectionnée(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {competences.map((comp) => (
                <Badge
                  key={comp.categoryId}
                  variant="secondary"
                  className="px-3 py-1.5 text-sm flex items-center gap-2"
                  style={{
                    backgroundColor: allCategories.find(c => c.id === comp.categoryId)?.color + '20',
                    color: allCategories.find(c => c.id === comp.categoryId)?.color
                  }}
                >
                  <span className="font-semibold">{comp.categoryName}</span>
                  {comp.subcategories.length > 0 && (
                    <span className="text-xs opacity-75">
                      ({comp.subcategories.length})
                    </span>
                  )}
                  <button
                    onClick={() => removeCompetence(comp.categoryId)}
                    className="hover:bg-black/10 rounded-full p-0.5"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Sélecteur de catégories */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Choisir mes Compétences</CardTitle>
          <CardDescription>
            Sélectionnez une ou plusieurs catégories et leurs sous-catégories
          </CardDescription>
        </CardHeader>
        <CardContent className="max-h-96 overflow-y-auto">
          <div className="space-y-2">
            {allCategories.map((category) => {
              const isSelected = isCategorySelected(category.id);
              const subcategories = getSubcategories(category.id);
              const isExpanded = expandedCategories[category.id];
              const selectedComp = competences.find(c => c.categoryId === category.id);

              return (
                <div key={category.id} className="border rounded-lg p-3">
                  {/* En-tête de catégorie */}
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1">
                      <Checkbox
                        id={`cat-${category.id}`}
                        checked={isSelected}
                        onCheckedChange={() => handleCategoryToggle(category)}
                      />
                      <Label
                        htmlFor={`cat-${category.id}`}
                        className="flex items-center gap-2 cursor-pointer flex-1"
                      >
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: category.color }}
                        />
                        <span className="font-medium">{category.name}</span>
                        {selectedComp?.subcategories.length > 0 && (
                          <Badge variant="outline" className="text-xs">
                            {selectedComp.subcategories.length} sous-catégorie(s)
                          </Badge>
                        )}
                      </Label>
                    </div>
                    
                    {/* Bouton d'expansion si la catégorie a des sous-catégories */}
                    {subcategories.length > 0 && (
                      <button
                        onClick={() => toggleCategory(category.id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                      </button>
                    )}
                  </div>

                  {/* Sous-catégories */}
                  {isExpanded && subcategories.length > 0 && (
                    <div className="mt-3 ml-8 space-y-2 border-l-2 border-gray-200 pl-4">
                      {subcategories.map((subcat) => (
                        <div key={subcat} className="flex items-center gap-2">
                          <Checkbox
                            id={`subcat-${category.id}-${subcat}`}
                            checked={isSubcategorySelected(category.id, subcat)}
                            onCheckedChange={() =>
                              handleSubcategoryToggle(category.id, category.name, subcat)
                            }
                          />
                          <Label
                            htmlFor={`subcat-${category.id}-${subcat}`}
                            className="text-sm cursor-pointer"
                          >
                            {subcat}
                          </Label>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default MultiCategorySelector;
